import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Search, ClipboardList, Package, FileCheck, Building2 } from 'lucide-react';
import logger from '../../utils/logger';
import VoiceSearchButton from './VoiceSearchButton';

/**
 * Global ⌘K search palette. Self-contained state; parent controls open/close.
 */
const SearchPalette = ({ open, onOpenChange }) => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    if (!query.trim()) { setResults(null); return undefined; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const r = await api.get(`/search?q=${encodeURIComponent(query.trim())}`);
        setResults(r.data);
      } catch (err) {
        logger.debug('search failed', err);
      } finally {
        setSearching(false);
      }
    }, 200);
    return () => clearTimeout(debounceRef.current);
  }, [query, open, api]);

  const goTo = (path) => {
    onOpenChange(false);
    setQuery('');
    navigate(path);
  };

  const countTotal = results
    ? (results.work_orders.length + results.assets.length
       + results.inspections.length + results.sites.length)
    : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl p-0" data-testid="global-search-dialog">
        <DialogHeader className="sr-only">
          <DialogTitle>Search</DialogTitle>
        </DialogHeader>
        <div className="border-b p-3">
          <div className="flex items-center gap-3">
            <Search className="h-5 w-5 text-muted-foreground flex-shrink-0" />
            <Input
              autoFocus
              placeholder="Search work orders, assets, inspections, sites..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="border-0 shadow-none focus-visible:ring-0 h-9 px-0"
              data-testid="global-search-input"
            />
            <VoiceSearchButton onTranscript={setQuery} paletteOpen={open} />
          </div>
        </div>
        <div className="max-h-96 overflow-y-auto p-2" data-testid="global-search-results">
          {searching && <div className="p-4 text-sm text-muted-foreground">Searching…</div>}
          {!searching && results && countTotal === 0 && (
            <div className="p-4 text-sm text-muted-foreground text-center">No results for &quot;{query}&quot;</div>
          )}
          {!searching && results && countTotal > 0 && (
            <>
              <ResultGroup title="Work Orders" Icon={ClipboardList} items={results.work_orders}
                render={(w) => (
                  <>
                    <span className="font-mono text-xs text-muted-foreground">{w.work_order_number}</span>
                    <span className="text-sm flex-1 truncate">{w.title}</span>
                    <Badge variant="outline" className="text-xs">{w.status}</Badge>
                  </>
                )}
                onSelect={(w) => goTo(`/work-orders/${w.id}`)}
              />
              <ResultGroup title="Assets" Icon={Package} items={results.assets}
                render={(a) => (
                  <>
                    <span className="text-sm flex-1 truncate">{a.name}</span>
                    {a.asset_tag && <span className="font-mono text-xs text-muted-foreground">{a.asset_tag}</span>}
                    <Badge variant="outline" className="text-xs">{a.status}</Badge>
                  </>
                )}
                onSelect={(a) => goTo(`/assets/${a.id}`)}
              />
              <ResultGroup title="Inspections" Icon={FileCheck} items={results.inspections}
                render={(i) => (
                  <>
                    <span className="font-mono text-xs text-muted-foreground">{i.inspection_number}</span>
                    <span className="text-sm flex-1 truncate">{i.title}</span>
                    <Badge variant="outline" className="text-xs">{i.status}</Badge>
                  </>
                )}
                onSelect={(i) => goTo(`/inspections/${i.id}`)}
              />
              <ResultGroup title="Sites" Icon={Building2} items={results.sites}
                render={(s) => (
                  <>
                    <span className="text-sm flex-1 truncate">{s.name}</span>
                    <span className="text-xs text-muted-foreground">{s.city}</span>
                  </>
                )}
                onSelect={(s) => goTo(`/sites/${s.id}`)}
              />
            </>
          )}
          {!searching && !results && (
            <div className="p-6 text-center text-sm text-muted-foreground">
              Start typing to search across FacilityPro.
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const ResultGroup = ({ title, Icon, items, render, onSelect }) => {
  if (!items || items.length === 0) return null;
  return (
    <div className="mb-2">
      <div className="flex items-center gap-2 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        <Icon className="h-3 w-3" />
        {title}
      </div>
      {items.map((item) => (
        <button
          key={item.id}
          type="button"
          onClick={() => onSelect(item)}
          className="w-full text-left px-3 py-2 rounded hover:bg-muted flex items-center gap-2"
        >
          {render(item)}
        </button>
      ))}
    </div>
  );
};

export default SearchPalette;
